<template>
  <v-dialog
    v-model="dialog"
    fullscreen
    hide-overlay
    scrollable
    persistent
    lazy
    width="1024"
  >
    <v-card>
      <v-toolbar dense color="primary" dark>
        <v-toolbar-title>
          {{
            camera._id
              ? $t('titles.global.updateModel', { field: $tc('models.camera') })
              : $t('titles.global.newAModel', { field: $tc('models.camera') })
          }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn dark icon @click="cancel">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-container py-0>
          <v-layout row wrap>
            <v-flex md3 xs12 px-2>
              <v-text-field
                v-model="camera.name"
                v-validate="'required'"
                autofocus
                :readonly="!edit"
                :label="$t('fields.global.name') + ' (*)'"
                :data-vv-name="$t('fields.global.name')"
                :error-messages="errors.collect($t('fields.global.name'))"
              />
            </v-flex>
            <v-flex md9 xs12 px-2>
              <v-text-field
                v-model="camera.description"
                :readonly="!edit"
                :label="$t('titles.roles.description')"
              />
            </v-flex>
            <v-flex md2 xs12 px-2>
              <v-text-field
                v-model="camera.ip"
                v-validate="'required|ip'"
                :label="$t('fields.global.ip') + ' (*)'"
                :data-vv-name="$t('fields.global.ip')"
                :error-messages="errors.collect($t('fields.global.ip'))"
              />
            </v-flex>
            <v-flex md2 xs12 px-2>
              <v-text-field
                v-model="camera.username"
                v-validate="'required'"
                :label="$t('fields.global.username') + ' (*)'"
                :data-vv-name="$t('fields.global.username')"
                :error-messages="errors.collect($t('fields.global.username'))"
              />
            </v-flex>
            <v-flex md3 xs12 px-2>
              <v-text-field
                v-model="camera.password"
                v-validate="'required'"
                :label="$t('fields.global.password') + ' (*)'"
                :data-vv-name="$t('fields.global.password')"
                :error-messages="errors.collect($t('fields.global.password'))"
                :type="showPassword ? 'text' : 'password'"
                prepend-icon="lock"
                :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                @click:append="showPassword = !showPassword"
              />
            </v-flex>
            <v-flex md1 xs12 px-2>
              <v-text-field
                v-model="camera.rtspPort"
                v-validate="'required|numeric'"
                type="number"
                :label="$t('fields.global.rtspPort') + ' (*)'"
                :data-vv-name="$t('fields.global.rtspPort')"
                :error-messages="errors.collect($t('fields.global.rtspPort'))"
              />
            </v-flex>
            <v-flex md4 xs12 px-2>
              <v-text-field
                v-model="camera.rtspPath"
                v-validate="'required'"
                :label="$t('fields.global.rtspPath') + ' (*)'"
                :data-vv-name="$t('fields.global.rtspPath')"
                :error-messages="errors.collect($t('fields.global.rtspPath'))"
                :readonly="!edit"
              />
            </v-flex>
            <v-flex md4 xs12 px-2>
              <v-text-field
                v-model="camera.rtspUrl"
                v-validate="'required'"
                :label="$t('fields.global.rtspUrl') + ' (*)'"
                :data-vv-name="$t('fields.global.rtspUrl')"
                :error-messages="errors.collect($t('fields.global.rtspUrl'))"
                :readonly="!edit"
              />
            </v-flex>
            <v-flex md1 xs12 px-2>
              <v-text-field
                v-model="camera.httpPort"
                v-validate="'required|numeric'"
                type="number"
                :label="$t('fields.global.httpPort') + ' (*)'"
                :data-vv-name="$t('fields.global.httpPort')"
                :error-messages="errors.collect($t('fields.global.httpPort'))"
              />
            </v-flex>
            <v-flex md5 xs12 px-2>
              <v-text-field
                v-model="camera.httpRestartPath"
                v-validate="'required'"
                :label="$t('fields.global.httpRestartPath') + ' (*)'"
                :data-vv-name="$t('fields.global.httpRestartPath')"
                :error-messages="
                  errors.collect($t('fields.global.httpRestartPath'))
                "
              />
            </v-flex>
            <v-flex md2 xs12 px-2>
              <v-text-field
                v-model="camera.recordingSize"
                v-validate="'required|numeric'"
                type="number"
                :label="$t('fields.global.recordingSize') + ' (GB) (*)'"
                :data-vv-name="$t('fields.global.recordingSize')"
                :error-messages="
                  errors.collect($t('fields.global.recordingSize'))
                "
              />
            </v-flex>
            <v-flex md4 xs12 px-2>
              <v-autocomplete
                v-model="camera.stadiumId"
                v-validate="'required'"
                :readonly="!edit || !!stadiumId"
                :items="stadiumSelectList"
                :label="
                  $t('action.actions.select', {
                    field: $tc('models.stadium'),
                  }) + ' (*)'
                "
                :data-vv-name="$tc('models.stadium')"
                :error-messages="errors.collect($tc('models.stadium'))"
                :clearable="edit && !stadiumId"
                no-filter
                :loading="$store.state.stadiums.loading"
                :search-input.sync="searchStadium"
                @change="onChangeStadium"
              >
                <template #selection="data">
                  <v-chip
                    color="white"
                    :selected="data.selected"
                    class="chip--select-multi"
                  >
                    <v-avatar>
                      <img :src="data.item.photo" />
                    </v-avatar>
                    {{ data.item.text }}
                  </v-chip>
                </template>
                <template #item="data">
                  <v-list-tile-avatar>
                    <img :src="data.item.photo" />
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title v-html="data.item.text" />
                  </v-list-tile-content>
                </template>
              </v-autocomplete>
            </v-flex>
            <v-flex md4 xs12 px-2>
              <v-autocomplete
                v-model="camera.ugaId"
                v-validate="'required'"
                :disabled="!camera.stadiumId"
                :items="ugaSelectList"
                :label="
                  $t('action.actions.select', { field: $tc('models.rbpi') }) +
                  ' (*)'
                "
                :data-vv-name="$tc('models.rbpi')"
                :error-messages="errors.collect($tc('models.rbpi'))"
                :loading="$store.state.rbpi.loading"
                :search-input.sync="searchUga"
              >
                <template #selection="data">
                  <v-chip
                    color="white"
                    :selected="data.selected"
                    class="chip--select-multi"
                  >
                    {{ data.item.text }}
                  </v-chip>
                </template>
                <template #item="data">
                  <v-list-tile-content>
                    <v-list-tile-title v-html="data.item.text" />
                  </v-list-tile-content>
                </template>
              </v-autocomplete>
            </v-flex>
            <v-flex md1 xs12 px-2>
              <v-switch
                v-model="camera.active"
                :label="$t('fields.global.active')"
                color="primary"
                :readonly="!edit"
              />
            </v-flex>
          </v-layout>
          <v-layout row wrap justify-center>
            <v-flex md9 xs12>
              <v-card>
                <v-toolbar flat dense color="primary" dark>
                  <v-toolbar-title>{{
                    $tc('fields.global.recordingSchedules')
                  }}</v-toolbar-title>
                  <v-spacer />
                  <v-btn flat @click="openRecordingScheduleForm()">
                    {{ $t('action.buttons.new') }}
                  </v-btn>
                </v-toolbar>
                <v-card-text class="pa-0">
                  <v-data-table
                    :headers="headers"
                    :items="camera.recordingSchedules"
                    disable-initial-sort
                    :rows-per-page-items="[10]"
                  >
                    <template slot="items" slot-scope="props">
                      <td>
                        <button-state :state="props.item.active" />
                      </td>
                      <td>
                        {{
                          $t('fields.global.daysOfWeek.' + props.item.dayOfWeek)
                        }}
                      </td>
                      <td>
                        {{ props.item.startTime }}
                      </td>

                      <td>
                        {{ props.item.endTime }}
                      </td>
                      <td class="text-xs-center">
                        <v-icon
                          color="success"
                          class="mr-1"
                          @click="
                            openRecordingScheduleForm(props.item, props.index)
                          "
                        >
                          edit
                        </v-icon>

                        <v-icon
                          color="error"
                          @click="
                            camera.recordingSchedules.splice(props.index, 1)
                          "
                        >
                          delete
                        </v-icon>
                      </td></template
                    >
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>
          <v-layout
            v-if="camera._id"
            row
            wrap
            justify-center
            class="mt-4"
          >
            <v-flex md9 xs12>
              <v-card>
                <v-toolbar flat dense color="primary" dark>
                  <v-toolbar-title>{{
                    $tc('fields.global.autoRecordings')
                  }}</v-toolbar-title>
                </v-toolbar>
                <v-card-text class="pa-0">
                  <v-data-table
                    :headers="autoRecordingHeaders"
                    :items="camera.autoRecordings"
                    disable-initial-sort
                    :rows-per-page-items="[10]"
                  >
                    <template slot="items" slot-scope="props">
                      <td>
                        <button-state :state="props.item.active" />
                      </td>
                      <td>
                        {{ $tc('models.' + props.item.source) }}
                      </td>
                      <td>
                        <a
                          v-if="autoRecordingLink(props.item)"
                          :href="autoRecordingLink(props.item)"
                          target="_blank"
                          rel="noopener"
                        >
                          {{ props.item.objectId }}
                        </a>
                        <span v-else>{{ props.item.objectId }}</span>
                      </td>
                      <td>
                        {{ formatDateTime(props.item.startAt) }}
                      </td>
                      <td>
                        {{ formatDateTime(props.item.endAt) }}
                      </td>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <template v-if="edit">
          <span style="color: red">(*) &nbsp;</span>
          <span>{{ $t('action.messages.fieldRequired') }}</span>
        </template>
        <v-spacer />
        <template v-if="edit">
          <meta-button type="reset" :disabled="loading" @click="clear" />
          <meta-button
            v-if="!camera._id"
            type="create"
            :loading="loading"
            @click="submit()"
          />
          <meta-button
            v-else
            type="update"
            :loading="loading"
            @click="submit()"
          />
        </template>
        <meta-button type="cancel" @click="cancel" />
      </v-card-actions>
    </v-card>
    <dialog-recording-schedule
      v-model="recordingScheduleDialog"
      :schedule="schedule"
      @submit="submitRecordingScheduleForm"
      @cancel="cancelRecordingScheduleForm"
    />
  </v-dialog>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters, mapState } from 'vuex'
import { parseObject } from '@/helpers/utils'
import DialogRecordingSchedule from './DialogRecordingSchedule'

export default {
  name: 'CameraForm',
  components: {
    DialogRecordingSchedule,
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
      required: true,
    },
    edit: {
      type: Boolean,
      default: true,
      required: false,
    },
    stadiumId: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      camera: {
        recordingSchedules: [],
      },
      schedule: null,
      searchStadium: '',
      searchUga: '',
      showPassword: false,
      recordingScheduleDialog: false,
      index: -1,
    }
  },

  computed: {
    ...mapState({
      selected: (state) => state.cameras.selected,
      loading: (state) => state.cameras.loading,
    }),
    ...mapGetters({
      stadiumSelectList: 'stadiums/selectList',
      ugaSelectList: 'rbpi/selectList',
    }),
    headers() {
      const headers = [
        {
          text: this.$tc('fields.global.active'),
          sortable: false,
          value: 'active',
        },
        {
          text: this.$tc('fields.global.dayOfWeek'),
          sortable: false,
          value: 'dayOfWeek',
        },

        {
          text: this.$t('fields.global.startTime'),
          sortable: false,
          value: 'startTime',
        },
        {
          text: this.$t('fields.global.endTime'),
          sortable: false,
          value: 'endTime',
        },
        {
          text: this.$t('fields.global.actions'),
          align: 'center',
          sortable: false,
        },
      ]

      return headers
    },
    autoRecordingHeaders() {
      return [
        {
          text: this.$tc('fields.global.active'),
          sortable: false,
          value: 'active',
        },
        {
          text: this.$t('fields.global.source'),
          sortable: false,
          value: 'source',
        },
        {
          text: this.$t('fields.global.id'),
          sortable: false,
          value: 'objectId',
        },
        {
          text: this.$t('fields.global.startAt'),
          sortable: false,
          value: 'startAt',
        },
        {
          text: this.$t('fields.global.endAt'),
          sortable: false,
          value: 'endAt',
        },
      ]
    },
  },

  watch: {
    dialog(val) {
      if (!val) this.cancel()
    },
    selected: {
      handler: function (val) {
        this.setObject(val)
      },
      immediate: true,
      deep: true,
    },
    stadiumId(val) {
      if (val) {
        this.camera.stadiumId = val
      }
    },
    'camera.stadiumId': {
      handler: function (val) {
        if (val) {
          this.fetchUgas({ stadiumId: val })
        }
      },
      immediate: true,
      deep: true,
    },
    searchStadium(val) {
      if (!this.camera.stadiumId) {
        this.debounceFetchSelect('stadiums', { match: val })
      }
    },
  },
  mounted() {
    this.setObject(this.selected)
  },
  methods: {
    ...mapActions({
      setCreate: 'cameras/create',
      setUpdate: 'cameras/update',
      reset: 'cameras/reset',
      fetchStadiums: 'stadiums/fetchSelectList',
      fetchUgas: 'rbpi/fetchSelectList',
    }),
    setObject(camera) {
      this.camera = { ...parseObject(camera), recordingSchedules: [] }

      if (camera.recordingSchedules) {
        this.camera.recordingSchedules = [...camera.recordingSchedules]
      }

      this.camera.recordingSchedules.sort((a, b) => a.dayOfWeek - b.dayOfWeek)

      this.camera.autoRecordings = camera.autoRecordings
        ? [...camera.autoRecordings]
        : []

      if (this.camera.stadiumId) {
        this.fetchStadiums({ _id: this.camera.stadiumId })
      }

      if (this.stadiumId) this.camera.stadiumId = this.stadiumId
    },
    onChangeStadium() {
      this.camera.ugaId = ''
    },
    formatDateTime(value) {
      return value ? moment(value).format('DD/MM/YYYY HH:mm') : ''
    },
    autoRecordingLink(item) {
      const id = item.objectId
      if (!id) return null

      let route
      if (item.source === 'match') {
        route = { name: 'match-edit', params: { id } }
      } else if (item.source === 'training') {
        route = { name: 'training-form', params: { action: 'edit', id } }
      } else {
        return null
      }

      return this.$router.resolve(route).href
    },
    openRecordingScheduleForm(schedule, index) {
      this.schedule = schedule
      this.index = index !== undefined ? index : -1
      this.recordingScheduleDialog = true
    },
    submitRecordingScheduleForm(schedule) {
      if (this.index != -1) {
        this.camera.recordingSchedules.splice(this.index, 1, schedule)
      } else {
        this.camera.recordingSchedules.push(schedule)
      }
      this.camera.recordingSchedules.sort((a, b) => a.dayOfWeek - b.dayOfWeek)
      this.cancelRecordingScheduleForm()
    },
    cancelRecordingScheduleForm() {
      this.recordingScheduleDialog = false
      this.schedule = null
      this.index = -1
    },
    submit() {
      this.validate().then((valid) => {
        if (!valid) return
        let payload = Object.assign({}, this.camera)
        if (!payload._id) {
          if (this.stadiumId) payload.stadiumId = this.stadiumId
          this.setCreate(payload).then((_) => {
            this.cancel()
          })
        } else {
          this.setUpdate(payload).then((_) => {
            this.cancel()
          })
        }
      })
    },
    onChangeRtspPath(rtspPath) {
      if (rtspPath) this.camera.rtspPath = rtspPath.trim()
    },
    cancel() {
      this.$emit('close')
      this.clear()
    },
    clear() {
      this.$nextTick((_) => {
        this.reset()
        this.$validator.reset()
      })
    },
  },
}
</script>
